/**
 * The frontend payment-processor seam (tracker item 38, "Frontend seam").
 *
 * The card form of a processor is one PaymentFormAdapter in its own folder
 * (adapters/<name>/). Pages (the public pay form, the staff "add card"
 * dialog) only know this contract and the registry; they never load a
 * processor script or read processor-specific config keys. The adapter loads
 * its script lazily in load(), so nothing processor-related is fetched until
 * a card form is actually shown.
 *
 * Card numbers never reach this app: the processor's own fields collect them
 * and tokenize() returns a one-time token that the backend charges or saves.
 */
import { PaymentClientConfig } from '../../../api/types';

export interface TokenizeDetails {
  /** Amount about to be charged (2 dp), for processors that verify the buyer */
  amount?: string;
  /** CHARGE (pay now) or STORE (save a card on file) */
  intent?: 'CHARGE' | 'STORE';
}

export interface PaymentFormAdapter {
  /** The provider id this adapter serves (matches clientConfig.provider) */
  readonly provider: string;
  /** Load the processor's browser SDK and initialize it with the non-secret config. */
  load(clientConfig: PaymentClientConfig): Promise<void>;
  /** Render the card fields into the container (after load). */
  render(container: HTMLElement): Promise<void>;
  /** Tokenize what the customer typed; throws PaymentFormError with a message to show. */
  tokenize(details?: TokenizeDetails): Promise<{ sourceToken: string }>;
  /** Remove the fields and listeners. Safe to call more than once. */
  destroy(): void;
}

/** A problem to show next to the card form (invalid card number, SDK failed to load, ...). */
export class PaymentFormError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PaymentFormError';
  }
}

/** One registered adapter: its provider id and a factory (a fresh instance per form). */
export interface PaymentFormAdapterRegistration {
  provider: string;
  create: () => PaymentFormAdapter;
}

const scriptLoads = new Map<string, Promise<void>>();

/**
 * Load an external script once per page (shared by adapters). Resolves when
 * it has loaded; a failure is not cached, so a retry can try again.
 */
export function loadScriptOnce(src: string): Promise<void> {
  const existing = scriptLoads.get(src);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoads.delete(src);
      script.remove();
      reject(new PaymentFormError('The card form could not be loaded. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });
  scriptLoads.set(src, promise);
  return promise;
}
